/**
 * Auto-schedule — Generate draft weekly schedule from werkroosters
 */

import type { Staff, LeaveEntry } from '../stores/workforce-store';
import type { ScheduleEntry, AvailabilityEntry, ShiftType } from '../stores/schedule-store';
import { getDutchHolidays } from './dutch-holidays';
import { snijVenster, vensterLabel } from './dagdelen';

export interface GeneratedShift {
  staff_id: string;
  staff_name: string;
  date: string;
  shift_type: ShiftType;
  time_from: string;
  time_to: string;
  source: 'werkrooster';
}

export interface SkippedShift {
  staff_name: string;
  date: string;
  reason: string;
}

export interface GenerateResult {
  shifts: GeneratedShift[];
  skipped: SkippedShift[];
}

/**
 * Generate a draft schedule for a week based on staff werkroosters.
 * Skips days where: shift already exists, leave, holiday, not available.
 */
export function generateWeekSchedule(
  staffList: Staff[],
  weekDates: string[],
  existingSchedule: ScheduleEntry[],
  leaveEntries: LeaveEntry[],
  availability: AvailabilityEntry[],
): GenerateResult {
  const shifts: GeneratedShift[] = [];
  const skipped: SkippedShift[] = [];

  // Get holidays for the year range covered by weekDates
  const years = new Set(weekDates.map(d => parseInt(d.substring(0, 4))));
  const holidays = new Set<string>();
  for (const year of years) {
    for (const h of getDutchHolidays(year)) {
      holidays.add(h.date);
    }
  }

  for (const staff of staffList) {
    if (!staff.is_active || !staff.werkrooster || staff.werkrooster.length === 0) continue;

    // Determine shift_type from team
    const shiftType: ShiftType = (staff.team === 'wasserij' || staff.team === 'route' || staff.team === 'kantoor')
      ? staff.team
      : 'kantoor';

    for (const date of weekDates) {
      const d = new Date(date + 'T00:00:00');
      const dow = d.getDay(); // 0=zo, 1=ma, ..., 6=za

      // Check if werkrooster has this day
      const roosterDay = staff.werkrooster.find(w => w.day === dow);
      if (!roosterDay) continue;

      // Check if shift already exists
      const hasShift = existingSchedule.some(
        s => s.staff_id === staff.id && s.date === date
      );
      if (hasShift) {
        skipped.push({ staff_name: staff.name, date, reason: 'Heeft al een dienst' });
        continue;
      }

      // Check for leave
      const hasLeave = leaveEntries.some(
        e => e.staff_id === staff.id && e.date === date
      );
      if (hasLeave) {
        skipped.push({ staff_name: staff.name, date, reason: 'Verlof' });
        continue;
      }

      // Check for holiday
      if (holidays.has(date)) {
        skipped.push({ staff_name: staff.name, date, reason: 'Feestdag' });
        continue;
      }

      // Check availability (if explicitly marked as not available)
      const avail = availability.find(
        a => a.staff_id === staff.id && a.date === date
      );
      if (avail && !avail.is_available) {
        skipped.push({ staff_name: staff.name, date, reason: 'Niet beschikbaar' });
        continue;
      }

      // Gedeeltelijke beschikbaarheid: snij het werkrooster af op het venster
      // dat de medewerker opgaf. Zonder deze stap krijgt iemand die alleen de
      // avond kan een dienst van 07:00-16:00 gegenereerd — het rooster is dan
      // niet onvolledig maar aantoonbaar fout, en niets meldt dat.
      const venster = snijVenster(
        roosterDay.from,
        roosterDay.to,
        avail?.time_from ?? null,
        avail?.time_to ?? null,
      );
      if (!venster) {
        skipped.push({
          staff_name: staff.name,
          date,
          reason: `Alleen beschikbaar ${vensterLabel(avail?.time_from ?? null, avail?.time_to ?? null)} — geen bruikbare overlap met ${roosterDay.from}-${roosterDay.to}`,
        });
        continue;
      }

      shifts.push({
        staff_id: staff.id,
        staff_name: staff.name,
        date,
        shift_type: shiftType,
        time_from: venster.from,
        time_to: venster.to,
        source: 'werkrooster',
      });
    }
  }

  return { shifts, skipped };
}
